"use client"

import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createPaymentIntent } from "../../../../redux/features/paymentSlice"
import { updateTransaction } from "../../../../redux/features/invoiceSlice"
import { useNavigate } from "react-router-dom"

const stripePromise = loadStripe(
  "pk_test_51QxnOSG2PEB65x1fFa8S34NoZXlPYFKwS1jd9nlfxP6ghYOCGCaNiHpr0meFd4hL3nMtPcmZKRx4wyJo9HE3sioF00YnOLMQ7E",
)

export default function CreditCardForm({ paymentInfo, applicationId }) {
  return (
    <div className="bg-[#FFFBF5] border border-[#CAC3BB] p-4 md:p-6 rounded-lg rounded-tl-none text-sm md:text-base">
      <h6 className="text-left ml-3 mb-2">Payment Amount: ${paymentInfo.amount}</h6>
      <Elements stripe={stripePromise}>
        <CheckoutForm paymentInfo={paymentInfo} applicationId={applicationId} />
      </Elements>
    </div>
  )
}

const CARD_STYLE = {
  style: {
    base: {
      fontSize: "16px",
      color: "#32325d",
      "::placeholder": { color: "#aab7c4" },
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    },
    invalid: {
      color: "#fa755a",
      iconColor: "#fa755a",
    },
  },
}

const CheckoutForm = ({ paymentInfo, applicationId }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { clientSecret } = useSelector((state) => state.payment)
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [zipcode, setZipcode] = useState("")
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [error, setError] = useState(null) // ✅ Added error state

  const handleSubmit = async (e) => {
    e.preventDefault()

    // ✅ Clear any previous errors
    setError(null)

    if (!stripe || !elements) return

    // ✅ Zip code validation with error state
    if (!/^\d{5,6}$/.test(zipcode)) {
      setError("Zip Code must be 5 or 6 digits")
      return
    }

    setLoading(true)

    try {
      // ✅ Call Redux action instead of direct fetch
      const clientSecret = await dispatch(createPaymentIntent({ amount: paymentInfo.amount, applicationId })).unwrap()

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
          billing_details: {
            address: { postal_code: zipcode },
          },
        },
      })

      if (stripeError) {
        // ✅ Set error state instead of alert
        setError(stripeError.message)
      } else if (paymentIntent?.status === "succeeded") {
        // After payment is successful and before setting showSuccessModal to true
        if (paymentIntent?.status === "succeeded") {
          // Send confirmation email
          try {
            const token = localStorage.getItem("token") // or the actual key you use to store the token

            if (!token) {
              console.error("No token found in localStorage. Please log in again.");
              navigate("/"); // Redirect to login if token is not found
            }

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/application/confirmationEmail`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`, // Include the token here
              },
              body: JSON.stringify({ applicationId }),
            })

            if (!response.ok) {
              console.error("Failed to send confirmation email:", await response.text())
            }
          } catch (error) {
            console.error("Error sending confirmation email:", error)
          }

          setShowSuccessModal(true)
          dispatch(
            updateTransaction({
              applicationId: applicationId,
              transactionId: paymentIntent.id,
              transactionDate: new Date().toISOString(),
              paymentMethod: "creditCard",
              file: null,
            }),
          )
          setZipcode("")
          elements.getElement(CardNumberElement)?.clear()
          elements.getElement(CardExpiryElement)?.clear()
          elements.getElement(CardCvcElement)?.clear()
        }
      }
    } catch (error) {
      console.error(error)
      // ✅ Set error state instead of alert
      setError(error.message || "Payment failed")
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <form>
        <label className="block mb-2 font-medium">Card Number</label>
        <div className="border bg-white p-2 rounded mb-4">
          <CardNumberElement options={CARD_STYLE} />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block mb-1">Expiry Date</label>
            <div className="border bg-white p-2 rounded">
              <CardExpiryElement options={CARD_STYLE} />
            </div>
          </div>
          <div>
            <label className="block mb-1">CVC</label>
            <div className="border bg-white p-2 rounded">
              <CardCvcElement options={CARD_STYLE} />
            </div>
          </div>
          <div>
            <label className="block mb-1">Zip Code</label>
            <input
              className="border p-1.5 rounded w-full"
              value={zipcode}
              onChange={(e) => {
                const val = e.target.value
                if (/^\d{0,6}$/.test(val)) {
                  setZipcode(val)
                }
                // ✅ Clear error when user starts typing
                if (error) setError(null)
              }}
              type="text"
              placeholder="12345"
              maxLength={6}
            />
          </div>
        </div>

        {/* ✅ Error message display above the button */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm font-medium">{error}</p>
          </div>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          disabled={!stripe || loading}
          className="bg-[#036494] hover:bg-[#075985] text-white py-2 rounded-full transition-colors px-5 mt-8 w-full"
        >
          {loading ? "Processing…" : `Pay $${paymentInfo.amount}`}
        </button>
      </form>
    </>
  )
}